<template>
    <!-- "Internal Notes" card, same look as the S&R Job Details one -->
    <div class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] bg-white">
        <div v-if="!hideTitle" class="flex items-center gap-1 pb-[15px]">
            <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
            <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
            <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">Internal Notes</p>
        </div>
        <!-- same notes box as S&R (nest_notes): own notes grey, others white; input row at the bottom -->
        <div :class="['flex flex-col border-1 border-[#CCCCCC] rounded-[10px] bg-white p-[5px] font-roboto', innerClass ? '' : 'laptop:min-h-[267px] max-h-[400px]']">
            <div ref="notesContainer"
                :class="['flex-1 overflow-x-hidden overflow-y-auto p-[5px] flex flex-col gap-[10px]', innerClass]">
                <div v-for="(item, index) in jobNotes" :key="index">
                    <div class="rounded-[10px] p-[10px] text-[12px] text-[#333333] break-words bg-white border-1 border-[#EFEFEF]">
                        <!-- note title: Android AppointmentInternalNotesAdapter shows the author's full_name (job_title) -->
                        <p v-if="noteTitle(item)" class="m-0 mb-[2px] text-[11px] text-[#999999]">{{ noteTitle(item) }}</p>
                        <p class="m-0 leading-[20px] text-[14px] whitespace-pre-line break-words">{{ item.notes }}</p>
                    </div>
                    <p class="m-0 mt-[4px] px-[2px] text-[10px] text-[#999999]">
                        {{ convertNotesTimsStampToDate(item.created_at_timestamp ?? item.created_at) }}
                    </p>
                </div>
                <p v-if="!jobNotes?.length" class="m-0 py-4 text-center text-[12px] text-[#999999]">No notes available</p>
            </div>
            <div
                class="flex items-end gap-2 mt-[5px] rounded-[10px] bg-white shadow-[0px_0px_20px_0px_#00000026] pl-[10px] pr-[5px] py-[5px]">
                <textarea ref="notesArea" v-model="notes" rows="1"
                    class="flex-1 min-w-0 resize-none border-0 outline-none bg-transparent py-[5px] min-h-[30px] max-h-[72px] hide-scrollbar leading-[20px] text-[14px] text-[#333333] placeholder:text-[#666666]"
                    placeholder="Write your Notes..." @input="autoResize" />
                <button type="button" :disabled="txtDisable" aria-label="Send note"
                    class="w-[30px] h-[30px] flex-shrink-0 rounded-[6px] bg-[#EFEFEF] border-0 flex items-center justify-center disabled:opacity-50"
                    @click="addNotesBtn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16" aria-hidden="true">
                        <path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"
                            d="M12.97 2.67a.5.5 0 0 0-.64-.64l-11 4a.5.5 0 0 0-.016.934l4.433 1.773l2.9-3.09l.707.707l-2.98 3.176l1.662 4.156a.5.5 0 0 0 .934-.015z" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { useToast } from "vue-toastification";
const toast = useToast();

export default {
    components: {},
    props: {
        innerClass: {
            type: String,
            default: '',
        },
        // Pass explicitly when the page is not an appointment (e.g. a quote's source appointment);
        // otherwise the appointment loaded in the store is used.
        appointmentId: {
            type: [Number, String],
            default: undefined,
        },
        // the Internal Notes modal has its own header
        hideTitle: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            modal: null,
            notes: "",
            jobNotes: [],
            txtDisable: true,
        };
    },
    computed: {
        ...mapGetters(["apptDetail", "notesData", "notesDataResponse"]),
        noteAppointmentId() {
            const id = this.appointmentId !== undefined ? this.appointmentId : this.apptDetail?.id;
            // API route expects an integer id; never call it with undefined/null
            return /^\d+$/.test(String(id ?? '')) ? Number(id) : null;
        },
    },

    watch: {
        noteAppointmentId: function () {
            this.jobNotes = [];
            this.getNotesData()
        },

        notesData: function () {
            this.notes = "";
            this.getNotesData()
        },

        notesDataResponse: function () {
            this.notes = "";
            this.jobNotes = this.notesDataResponse ?? [];
            this.scrollToBottom()

        },

        notes: function () {
            if (!this.isEmpty(this.notes)) {
                this.txtDisable = false;
            } else {
                this.txtDisable = true;
            }
        },
    },

    mounted() {
        this.getNotesData()
    },

    methods: {
        ...mapActions(["addManualQuoteNote", "getAppointmentNotes"]),
        autoResize() {
            const textarea = this.$refs.notesArea;
            textarea.style.height = "auto";
            textarea.style.height = Math.min(textarea.scrollHeight, 72) + "px";
            // 72px = approx 3 lines
        },
        resetTextareaHeight() {
            this.$nextTick(() => {
                const textarea = this.$refs.notesArea;
                if (textarea) {
                    textarea.style.height = "40px"; // 1 line height reset
                }
            });
        },
        getNotesData() {
            if (!this.noteAppointmentId) return;
            this.getAppointmentNotes({
                id: this.noteAppointmentId,
            });
        },

        addNotesBtn() {
            if (this.isValid() && this.noteAppointmentId) {
                this.addManualQuoteNote({
                    id: this.noteAppointmentId,
                    internal_notes: this.notes,
                });
            }
            this.notes = "";
            this.resetTextareaHeight();
        },
        // appointment notes are append-only, like Android (api/v3/pro has only list / store; G-23)
        isValid() {
            let res = true;
            if (this.isEmpty(this.notes)) {
                toast.error("Please enter note");
                res = false;
            }
            return res;
        },
        isEmpty(value) {
            return (
                value == null ||
                value.trim().length === 0 ||
                (value.constructor === Object && Object.keys(value).length === 0)
            );
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.notesContainer;
                if (el) {
                    el.scrollTop = el.scrollHeight;
                }
            });
        },
        noteTitle(item) {
            const name = item.full_name || [item.first_name, item.last_name].filter(Boolean).join(' ');
            return String(name || '').trim();
        },
        // API sends created_at as "YYYY-MM-DD HH:mm:ss.SSS" and created_at_timestamp as unix seconds (string)
        convertNotesTimsStampToDate(val) {
            const date = /^\d+$/.test(String(val)) ? moment.unix(Number(val)) : moment(val);
            return date.isValid() ? date.format("MM/DD/YYYY hh:mm A") : '';
        },
    },
};
</script>
